// ============================================================================
// @orgazone/tickets — BUILDERS DES 13 GABARITS (portés de ticket-gabarits.jsx).
// Chaque builder : (données métier, contexte émetteur) → TicketDocument.
// Contexte injecté (émetteur/logiciel = back-office §12) — jamais figé dans le code.
// Réf. SPEC §19.5.
// ============================================================================
import {
  band, barcode, cut, feed, frame, gap, row, rule, table, txt,
  type TicketDocument, type TicketOp,
} from './document';
import {
  emitterHeader, emitterLegal, fiscalBlock, payments, pieceMeta, saleLines, storeFooter, vatTable,
} from './blocks';
import { countItems, moneyMinor, ratePct, ventilateLines } from './format';
import type {
  JustificatifPart, MiseEnAttenteInput, OuvertureInput, PrelevementInput,
  RetoucheInput, SaleTicketInput, VoucherInput, ZInput,
} from './types';
import type { EmitterInfo, SoftwareInfo } from './types';

/** Contexte d'impression : émetteur + logiciel (back-office) + largeur papier. */
export interface TicketContext {
  emitter: EmitterInfo;
  soft: SoftwareInfo;
  /** 80 (SUNMI/Epson) par défaut, ~40 sur PAX. */
  paperWidthMm?: number;
}

/** Valeur du code-barres 1D de rappel = 12 derniers chiffres du n° de pièce (§19.5.3). */
export const recallValue = (num: string): string => num.replace(/\D/g, '').slice(-12);

const doc = (kind: string, fiscal: boolean, ctx: TicketContext, ops: TicketOp[]): TicketDocument => ({
  kind, fiscal, paperWidthMm: ctx.paperWidthMm ?? 80, ops: [...ops, feed(1), cut()],
});

// ---- 1-3. Famille vente : ticket de caisse · duplicata · ticket cadeau -------
export interface SaleTicketOptions {
  variant?: 'original' | 'duplicata' | 'gift';
  reprint?: number;
  motif?: string;
}
export function buildSaleTicket(sale: SaleTicketInput, ctx: TicketContext, opts: SaleTicketOptions = {}): TicketDocument {
  const { variant = 'original', reprint = 1, motif } = opts;
  const gift = variant === 'gift';
  const dup = variant === 'duplicata';
  const { emitter, soft } = ctx;
  const gd = sale.globalDiscountBp ?? 0;
  const loy = sale.loyaltyDiscountCents ?? 0; // remise fidélité (gain consommé) — déduite prorata par ligne (§12.1)
  // ⚠️ LA VENTILATION SCELLÉE FAIT FOI quand elle est fournie (cf. `SaleTicketInput.breakdown`).
  const brk = sale.breakdown ?? ventilateLines(sale.lines, gd, loy);

  const ops: TicketOp[] = [...emitterHeader(emitter)];
  if (dup) ops.push(band('DUPLICATA'));
  if (gift) ops.push(band('TICKET CADEAU'));
  ops.push(rule(), ...pieceMeta(sale, 'VENTE'), rule(), ...saleLines(sale.lines, !gift, gd, loy, sale.globalDiscountCents));

  if (!gift) {
    ops.push(...vatTable(brk, countItems(sale.lines)), ...payments(sale.payments));
    if (sale.loyalty) {
      ops.push(rule(),
        row('Fidélité — gain cumulé', `+${moneyMinor(sale.loyalty.earnedCents)}`),
        row('Solde fidélité disponible', moneyMinor(sale.loyalty.balanceCents)));
    }
  }
  if (dup) {
    // Avis de réimpression dans un ENCADRÉ (= `tk-warn` de la maquette).
    ops.push(frame(`Réimpression n°${reprint} de la pièce ${sale.num}.${motif ? ` Motif : ${motif}.` : ''} Reproduction conforme à l'original.`));
  }
  if (gift) {
    ops.push(barcode(sale.num, true)); // code-barres = numéro COURT complet (scannable + matche la recherche du journal)
    ops.push(frame('NE VAUT PAS JUSTIFICATIF DE PAIEMENT — document non fiscal. À présenter pour tout échange.'));
    ops.push(...storeFooter(emitter, true));
  } else {
    // Code-barres de rappel sans texte lisible (display=false maquette) — SEUL code scannable ;
    // encode le numéro COURT complet (`T-S1R1-…`) → scanner le ticket retrouve la vente au journal.
    ops.push(barcode(sale.num, false));
    ops.push(...fiscalBlock(soft, { num: sale.num, register: sale.register, signature: sale.signature }, dup ? reprint : 1, dup ? 'Duplicata' : 'Ticket'));
    ops.push(...storeFooter(emitter, true), ...emitterLegal(emitter));
  }
  return doc(gift ? 'gift' : dup ? 'duplicata' : 'ticket', !gift, ctx, ops);
}

// ---- 3bis. Facture directe (F-, §30.7.1 / §8ter) ----------------------------
/** Identité du client FACTURÉ (mention légale) — seules les données EXISTANTES s'impriment (§23.0). */
export interface FactureClientBlock {
  name: string;
  /** Raison sociale (B2B) — imprimée AU-DESSUS du nom du porteur quand présente. */
  companyName?: string;
  address?: string[];
  siret?: string;
  tvaNum?: string;
}

/** Une ligne du bloc « Facturé à », telle que la facture l'imprime. `strong` : la ligne qui nomme le client facturé. */
export interface PrintedClientLine {
  kind: 'company' | 'person' | 'address' | 'siret' | 'vat';
  text: string;
  strong: boolean;
}

/**
 * LES LIGNES DU BLOC « FACTURÉ À », dans l'ordre et les mots où la facture les imprime — thermique et A4.
 * ⚠️ UNE SOURCE POUR LES DEUX FACTURES ET POUR L'APERÇU du back-office (« Sur la facture » de la fiche client) : un aperçu
 *    qui recomposerait ces lignes finirait par annoncer une autre facture que celle qui sort.
 * ⚠️ SEULES LES DONNÉES EXISTANTES S'IMPRIMENT (§23.0) : aucune ligne vide, aucun « SIRET » sans numéro.
 */
export function printedClientLines(fClient: FactureClientBlock): PrintedClientLine[] {
  const lines: PrintedClientLine[] = [];
  if (fClient.companyName) lines.push({ kind: 'company', text: fClient.companyName, strong: true });
  lines.push({ kind: 'person', text: fClient.name, strong: !fClient.companyName });
  for (const a of fClient.address ?? []) if (a) lines.push({ kind: 'address', text: a, strong: false });
  if (fClient.siret) lines.push({ kind: 'siret', text: `SIRET ${fClient.siret}`, strong: false });
  if (fClient.tvaNum) lines.push({ kind: 'vat', text: `TVA ${fClient.tvaNum}`, strong: false });
  return lines;
}

/**
 * Facture DIRECTE de caisse : même contenu prouvable que le ticket (lignes, ventilation §30.3,
 * règlements, bloc fiscal signé) + le bloc « Facturé à » (identité client, mention légale qui
 * DISTINGUE la facture du ticket). Support THERMIQUE — l'édition A4 est un canal d'impression
 * séparé (chantier module d'impression système), le CONTENU est identique.
 */
export function buildFacture(
  sale: SaleTicketInput,
  fClient: FactureClientBlock,
  ctx: TicketContext,
  opts: { variant?: 'original' | 'duplicata'; reprint?: number; motif?: string; creditNoteOf?: string; cancelsCreditNote?: string } = {},
): TicketDocument {
  const { variant = 'original', reprint = 1, motif, creditNoteOf, cancelsCreditNote } = opts;
  const dup = variant === 'duplicata';
  // ⚠️ LA FACTURE D'AVOIR CITE LA FACTURE QU'ELLE CORRIGE (CGI art. 242 nonies A) — c'est ce qui la distingue d'une
  //    facture négative sans origine.
  const word = creditNoteOf ? 'Facture d’avoir' : 'Facture';
  const { emitter, soft } = ctx;
  const gd = sale.globalDiscountBp ?? 0;
  const loy = sale.loyaltyDiscountCents ?? 0;
  // ⚠️ LA VENTILATION SCELLÉE FAIT FOI quand elle est fournie (cf. `SaleTicketInput.breakdown`).
  const brk = sale.breakdown ?? ventilateLines(sale.lines, gd, loy);

  const ops: TicketOp[] = [...emitterHeader(emitter), band(creditNoteOf ? 'FACTURE D’AVOIR' : 'FACTURE')];
  if (dup) ops.push(band('DUPLICATA'));
  ops.push(rule(), ...pieceMeta(sale, creditNoteOf ? 'AVOIR' : 'FACTURE', word));
  if (creditNoteOf) ops.push(row('Avoir sur la facture', creditNoteOf));
  // ⚠️ L'ANNULATION D'UNE FACTURE D'AVOIR EST UNE FACTURE (décision utilisateur du 2026-10-27) : un avoir ne s'annule
  //    pas, on refacture — en citant l'avoir qu'on annule.
  if (cancelsCreditNote) ops.push(row('Annule la facture d’avoir', cancelsCreditNote));
  ops.push(rule());
  // Bloc CLIENT — la raison d'être du document : sans identité client, pas de facture (garde §8ter).
  ops.push(txt('Facturé à', { bold: true, size: 'sm' }));
  for (const l of printedClientLines(fClient)) {
    ops.push(txt(l.text, l.strong ? { bold: true } : l.kind === 'person' ? { size: 'sm' } : { size: 'sm', muted: true }));
  }
  ops.push(rule(), ...saleLines(sale.lines, true, gd, loy, sale.globalDiscountCents), ...vatTable(brk, countItems(sale.lines)), ...payments(sale.payments));
  if (dup) ops.push(frame(`Réimpression n°${reprint} de la pièce ${sale.num}.${motif ? ` Motif : ${motif}.` : ''} Reproduction conforme à l'original.`));
  ops.push(barcode(sale.num, false));
  ops.push(...fiscalBlock(soft, { num: sale.num, register: sale.register, signature: sale.signature }, dup ? reprint : 1, dup ? 'Duplicata' : word));
  ops.push(...storeFooter(emitter, true), ...emitterLegal(emitter));
  return doc('facture', true, ctx, ops);
}

// ---- 4. Justificatif de paiement (règlement partagé) -------------------------
export function buildJustificatif(sale: SaleTicketInput, part: JustificatifPart, ctx: TicketContext): TicketDocument {
  const { emitter, soft } = ctx;
  const ops: TicketOp[] = [
    ...emitterHeader(emitter, true),
    band('JUSTIFICATIF DE PAIEMENT'),
    rule(),
    row('Justificatif', part.num),
    row("Ticket d'origine", sale.num),
    row(sale.date, sale.time),
    row('Réglé par', part.client),
    rule(true),
    row(part.method, moneyMinor(part.amountCents), true),
    // Mention « non valable pour encaissement » dans un ENCADRÉ (= `tk-warn` de la maquette).
    frame('JUSTIFICATIF NON VALABLE POUR ENCAISSEMENT. Part réglée sur un règlement partagé.'),
    barcode(part.num, false), // numéro COURT complet (scannable)
    ...fiscalBlock(soft, { num: part.num, register: sale.register, signature: sale.signature }, 1, 'Justificatif'),
    ...emitterLegal(emitter),
  ];
  return doc('justificatif', true, ctx, ops);
}

// ---- 5-8. Titres (chèque cadeau, avoir, acompte, bon fidélité) — NON signés ---
export function buildVoucher(voucher: VoucherInput, ctx: TicketContext): TicketDocument {
  const { emitter } = ctx;
  const ops: TicketOp[] = [
    ...emitterHeader(emitter, true),
    band(voucher.bandLabel),
    rule(),
    row('N°', voucher.num),
    row('Émis le', voucher.date),
  ];
  if (voucher.issuedTo) ops.push(row('Au profit de', voucher.issuedTo));
  if (voucher.source) ops.push(row('Origine', voucher.source));
  ops.push(
    gap(true),
    // Un seul montant affiché = celui qui a du SENS : « Montant » à l'émission, « Solde restant » au reliquat
    // (porté par `amountLabel`). ⚠️ Ancienne ligne « dont solde restant » RETIRÉE (redondante/absurde quand le
    // montant affiché EST déjà le solde — retour terrain 2026-07).
    txt(voucher.amountLabel ?? 'Montant', { align: 'center', size: 'sm', muted: true }),
    txt(moneyMinor(voucher.amountCents), { align: 'center', size: 'xl' }),
  );
  ops.push(
    gap(true),
    barcode(voucher.code, true),
    band("À SCANNER EN CAISSE LORS DE L'UTILISATION", true),
  );
  if (voucher.validity) ops.push(txt(voucher.validity, { align: 'center', size: 'sm', muted: true }));
  if (voucher.expiry) ops.push(txt(`Valable jusqu'au ${voucher.expiry}`, { align: 'center', bold: true, size: 'sm' }));
  ops.push(rule());
  if (voucher.originPiece) {
    ops.push(txt(`Titre émis sur la pièce ${voucher.originPiece}`, { align: 'center', size: 'sm', muted: true }));
    ops.push(txt("(ticket de caisse signé NF525). Ce titre est un instrument, non l'encaissement.", { align: 'center', size: 'sm', muted: true }));
  } else {
    ops.push(txt('Titre — instrument à présenter en caisse.', { align: 'center', size: 'sm', muted: true }));
  }
  ops.push(...emitterLegal(emitter));
  return doc('titre', false, ctx, ops);
}

// ---- 9. Mise en attente (non fiscal — code-barres de rappel) ------------------
export function buildMiseEnAttente(input: MiseEnAttenteInput, ctx: TicketContext): TicketDocument {
  const { emitter } = ctx;
  const gd = input.globalDiscountBp ?? 0;
  const brk = input.breakdown ?? ventilateLines(input.lines, gd);
  const recall = input.recallCode ?? `HOLD${input.shortNum ?? ''}${recallValue(input.num)}`;
  const ops: TicketOp[] = [
    ...emitterHeader(emitter, true),
    band('VENTE EN ATTENTE'),
    rule(),
    row('Mise en attente', `${input.date}  ${input.time}`),
    row(`Caisse n°${input.register}`, `Vendeur : ${input.seller}`),
    row('Client', input.client ? input.client.name : 'N/C'),
    rule(),
    ...saleLines(input.lines, true, gd, 0, input.globalDiscountCents),
    rule(true),
    row(`Total provisoire (${countItems(input.lines)} art.)`, moneyMinor(brk.totalTtcCents), true),
    gap(true),
    barcode(recall, true),
    band('SCANNEZ POUR REPRENDRE LA VENTE', true),
    // Mention « non fiscal » dans un ENCADRÉ (= `tk-warn` de la maquette).
    frame("NON FISCAL — ne vaut pas ticket de caisse. Aucun encaissement n'a été réalisé."),
    ...storeFooter(emitter, false),
  ];
  return doc('attente', false, ctx, ops);
}

// ---- 10. Ticket Z — clôture de caisse (fiscal) -------------------------------
export function buildTicketZ(z: ZInput, ctx: TicketContext): TicketDocument {
  const { emitter, soft } = ctx;
  const totVat = z.vat.reduce((s, v) => s + v.vatCents, 0);
  const totBase = z.vat.reduce((s, v) => s + v.htCents, 0);
  const ops: TicketOp[] = [
    ...emitterHeader(emitter, true),
    band('TICKET Z — CLÔTURE'),
    rule(),
    row('Clôture n°', z.num),
    row('Période', z.period),
    row(`Caisse n°${z.register}`, `Op. : ${z.operator}`),
    row('Ouverture', z.open),
    row('Fermeture', z.close),
    rule(true),
    row(`CA TTC (${z.nbTickets} tickets)`, moneyMinor(z.caCents), true),
    ...(z.titlesIssuedCents ? [row('Titres émis (hors CA)', moneyMinor(z.titlesIssuedCents))] : []),
    row('Articles vendus', String(z.nbItems)),
    rule(),
    txt('VENTILATION TVA', { bold: true, size: 'sm' }),
    table(
      ['TVA', 'Base HT', 'TVA'],
      z.vat.map(v => [ratePct(v.rateBp), moneyMinor(v.htCents), moneyMinor(v.vatCents)]),
      ['Total', moneyMinor(totBase), moneyMinor(totVat)],
      ['left', 'right', 'right'],
    ),
    rule(),
    txt('ENCAISSEMENTS', { bold: true, size: 'sm' }),
    ...z.pay.map(p => row(p.label, moneyMinor(p.amountCents))),
    rule(),
    txt('TRÉSORERIE ESPÈCES', { bold: true, size: 'sm' }),
    row('Espèces à l\'ouverture', moneyMinor(z.fondCents)),
    row('Ventes espèces', moneyMinor(z.cashSalesCents)),
    ...((z.cashInCents ?? 0) !== 0 ? [row('Apports', `+${moneyMinor(z.cashInCents ?? 0)}`)] : []),
    row('Prélèvements', `-${moneyMinor(z.prelevementsCents)}`),
    row('Théorique en caisse', moneyMinor(z.theoCents), true),
    row('Compté', z.notCounted ? 'non compté' : moneyMinor(z.compteCents)),
    row('Écart', z.notCounted ? '—' : moneyMinor(z.varianceCents), true),
    rule(),
    row('Grand Total perpétuel', moneyMinor(z.gtPerpetuelCents)),
    ...fiscalBlock(soft, { num: z.num, register: z.register, signature: z.signature }, 1, 'Clôture Z'),
    ...emitterLegal(emitter),
  ];
  return doc('z', true, ctx, ops);
}

// ---- 11. Ouverture de caisse (fond de caisse) --------------------------------
export function buildOuverture(o: OuvertureInput, ctx: TicketContext): TicketDocument {
  const { emitter, soft } = ctx;
  const ops: TicketOp[] = [
    ...emitterHeader(emitter, true),
    band('OUVERTURE DE CAISSE'),
    rule(),
    row(o.date, o.time),
    row(`Caisse n°${o.register}`, `Op. : ${o.operator}`),
    rule(true),
    row('Pièces', moneyMinor(o.coinsCents)),
    row('Billets', moneyMinor(o.notesCents)),
    row('FOND DE CAISSE', moneyMinor(o.fondCents), true),
  ];
  if (o.obs) ops.push(rule(), txt(`Obs. : ${o.obs}`, { size: 'sm' }));
  ops.push(
    ...fiscalBlock(soft, { num: o.num, register: o.register, signature: o.signature }, 1, 'Ouverture'),
    ...emitterLegal(emitter),
  );
  return doc('ouverture', true, ctx, ops);
}

// ---- 12. Prélèvement d'espèces (sortie en journée) ---------------------------
export function buildPrelevement(p: PrelevementInput, ctx: TicketContext): TicketDocument {
  const { emitter, soft } = ctx;
  const ops: TicketOp[] = [
    ...emitterHeader(emitter, true),
    band("PRÉLÈVEMENT D'ESPÈCES"),
    rule(),
    row('N°', p.num),
    row(p.date, p.time),
    row(`Caisse n°${p.register}`, `Op. : ${p.operator}`),
    row('Type', p.type),
    rule(true),
    row('MONTANT PRÉLEVÉ', `-${moneyMinor(p.amountCents)}`, true),
    row('Espèces avant', moneyMinor(p.beforeCents)),
    row('Espèces après', moneyMinor(p.afterCents)),
  ];
  if (p.motif) ops.push(rule(), txt(`Motif : ${p.motif}`, { size: 'sm' }));
  ops.push(
    ...fiscalBlock(soft, { num: p.num, register: p.register, signature: p.signature }, 1, 'Prélèvement'),
    ...emitterLegal(emitter),
  );
  return doc('prelevement', true, ctx, ops);
}

// ---- 13. Bon de retouche (service atelier — non fiscal) ----------------------
export function buildRetouche(r: RetoucheInput, ctx: TicketContext, copy: 'Client' | 'Atelier' = 'Client'): TicketDocument {
  const { emitter } = ctx;
  const ops: TicketOp[] = [
    ...emitterHeader(emitter, true),
    band('BON DE RETOUCHE'),
    txt(`Exemplaire ${copy.toLowerCase()}`, { align: 'center', size: 'sm', muted: true }),
    rule(),
    row('N° service', r.num),
    row('Déposé le', r.date),
    row('Client', r.client),
    rule(),
    txt(r.article, { bold: true }),
  ];
  if (r.size) ops.push(txt(r.size, { size: 'sm', muted: true }));
  ops.push(
    txt(r.presta),
    row('Prix prestation', moneyMinor(r.priceCents)),
    rule(true),
    txt('À RETIRER LE', { align: 'center', size: 'sm', muted: true }),
    txt(r.pickup, { align: 'center', size: 'xl' }),
  );
  // Note atelier dans un ENCADRÉ (= `tk-warn` de la maquette).
  if (r.obs) ops.push(frame(`Note atelier : ${r.obs}`));
  ops.push(
    barcode(r.code, true),
    band('SCANNER POUR RETROUVER LA RETOUCHE', true),
  );
  // Message boutique du bas de bon (Réglages > Ticket « Info service retouche »).
  if (r.info) ops.push(txt(r.info, { align: 'center', size: 'sm', muted: true }));
  ops.push(...storeFooter(emitter, false));
  return doc('retouche', false, ctx, ops);
}
